import { FC, useState } from 'react';

import { AccordionDetails, Typography } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import {
  HeadIconBox,
  Item,
  ItemBrokerBox,
  ItemContent,
  ItemContentCheckboxesBox,
  ItemContentCheckboxItem,
  ItemContentRow,
  ItemField,
  ItemHead,
  WhatsAppElement,
} from './styled';
import { CalendarIcon, CheckboxGreenIcon, UserIcon, WhatsAppIcon } from '@/components';
import { COLORS } from '@/constants';
import { Link } from '@/components/shared/UI/Link';
import { BrokerData, BrokerRequest } from '@/types';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';

type BrokerRequestItemProps = {
  data: BrokerRequest;
  brokerData: BrokerData;
};

export const BrokerRequestItem: FC<BrokerRequestItemProps> = ({ data, brokerData }) => {
  const t = useTranslations();

  const [expanded, setExpanded] = useState(false);

  const isCommentExist = data?.comment !== '';

  return (
    <Item expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <ItemHead expandIcon={expanded ? <ExpandLess /> : <ExpandMore />}>
        <ItemBrokerBox>
          <HeadIconBox>
            <CalendarIcon width={16} height={16} stroke={COLORS['brown/300']} />
            <Typography
              fontSize={11}
              fontWeight={500}
              lineHeight={1.6}
              color={COLORS['neutral/600']}
            >
              {dayjs(data.createdAt).format('DD.MM.YYYY HH:mm')}
            </Typography>
          </HeadIconBox>

          <HeadIconBox>
            <UserIcon />
            <Typography
              fontSize={12}
              fontWeight={600}
              lineHeight={'145%'}
              color={COLORS['neutral/900']}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {brokerData?.name}
            </Typography>
          </HeadIconBox>
        </ItemBrokerBox>

        <Typography
          color={COLORS['primary/600']}
          sx={{ textTransform: 'none' }}
          fontSize={12}
          lineHeight={'120%'}
          fontWeight={500}
          textAlign={'end'}
          marginLeft={'auto'}
          marginY={'auto'}
        >
          {t('details')}
        </Typography>
      </ItemHead>

      <AccordionDetails>
        <ItemContent>
          <ItemContentRow>
            <ItemField>
              <Typography
                color={COLORS['neutral/500']}
                fontSize={10}
                lineHeight={'140%'}
                fontWeight={400}
              >
                {t('watchList.request.email')}
              </Typography>
              <Typography
                color={COLORS['primary/500']}
                fontSize={12}
                lineHeight={'140%'}
                fontWeight={500}
              >
                <Link href={`mailto:${data.email}`} style={{ color: COLORS['primary/500'] }}>
                  {data.email}
                </Link>
              </Typography>
            </ItemField>

            <ItemField>
              <Typography
                color={COLORS['neutral/500']}
                fontSize={10}
                lineHeight={'140%'}
                fontWeight={400}
              >
                {data.sendWhatsapp
                  ? t('watchList.request.telWhatsApp')
                  : t('watchList.request.tel')}
              </Typography>
              <Typography
                color={COLORS['primary/900']}
                fontSize={12}
                lineHeight={'140%'}
                fontWeight={500}
              >
                <Link
                  href={`tel:${data.phone?.replace(/\s/g, '')}`}
                  style={{ color: COLORS['primary/900'] }}
                >
                  {data.phone}
                </Link>
              </Typography>
            </ItemField>

            {data.sendWhatsapp && (
              <WhatsAppElement>
                <WhatsAppIcon />
                <Typography fontSize={14} fontWeight={600}>
                  {t('watchList.request.inquiry')}
                </Typography>
              </WhatsAppElement>
            )}
          </ItemContentRow>
          <ItemField>
            <Typography
              color={COLORS['neutral/500']}
              fontSize={10}
              lineHeight={'140%'}
              fontWeight={400}
            >
              {t('watchList.request.comment')}
            </Typography>
            <Typography
              color={COLORS['primary/900']}
              fontSize={12}
              lineHeight={'140%'}
              fontWeight={500}
              sx={{
                opacity: isCommentExist ? 1 : 0.4,
              }}
            >
              {isCommentExist ? data?.comment : t('noComments')}
            </Typography>
          </ItemField>

          <ItemContentCheckboxesBox>
            {data.requestCall && (
              <ItemContentCheckboxItem>
                <CheckboxGreenIcon />
                <Typography color={COLORS['primary/900']} fontSize={12} fontWeight={500}>
                  {t('housingCardDetails.callRequest')}
                </Typography>
              </ItemContentCheckboxItem>
            )}
            {data.gdprAccepted && (
              <ItemContentCheckboxItem>
                <CheckboxGreenIcon />
                <Typography color={COLORS['primary/900']} fontSize={12} fontWeight={500}>
                  {t('housingCardDetails.acceptGdpr')}
                </Typography>
              </ItemContentCheckboxItem>
            )}
          </ItemContentCheckboxesBox>
        </ItemContent>
      </AccordionDetails>
    </Item>
  );
};
